import React from 'react';
import { useNavigate } from 'react-router-dom';
import { StorageInfo } from '../hooks/useStorageInfo';

interface StorageWarningBannerProps {
  storageInfo: StorageInfo;
  // 'global' = shown in header on all pages (95%+ only)
  // 'local'  = shown on Uploads page (80%+)
  context: 'global' | 'local';
}

const StorageWarningBanner: React.FC<StorageWarningBannerProps> = ({ storageInfo, context }) => {
  const navigate = useNavigate();
  const { usedGB, limitGB, usagePercent, nearLimit, urgentLimit } = storageInfo;

  // Global banner: only show at 95%+
  if (context === 'global' && !urgentLimit) return null;

  // Local banner: show at 80%+
  if (context === 'local' && !nearLimit) return null;

  const isUrgent = urgentLimit;
  const freeGB = (limitGB - usedGB).toFixed(1);

  const bannerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: context === 'global' ? '10px 24px' : '14px 20px',
    background: isUrgent
      ? 'linear-gradient(135deg, rgba(239,68,68,0.15), rgba(239,68,68,0.08))'
      : 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(245,158,11,0.08))',
    borderBottom: context === 'global'
      ? `1px solid ${isUrgent ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`
      : 'none',
    borderRadius: context === 'local' ? '10px' : '0',
    border: context === 'local'
      ? `1px solid ${isUrgent ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`
      : undefined,
    marginBottom: context === 'local' ? '20px' : '0',
    flexWrap: 'wrap' as const,
  };

  const iconStyle: React.CSSProperties = {
    fontSize: context === 'global' ? '16px' : '18px',
    flexShrink: 0,
  };

  const textStyle: React.CSSProperties = {
    flex: 1,
    fontSize: context === 'global' ? '13px' : '14px',
    color: isUrgent ? '#FCA5A5' : '#FCD34D',
    lineHeight: 1.4,
  };

  const progressTrackStyle: React.CSSProperties = {
    width: context === 'global' ? '80px' : '120px',
    height: '6px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '999px',
    overflow: 'hidden',
    flexShrink: 0,
  };

  const progressFillStyle: React.CSSProperties = {
    height: '100%',
    width: `${Math.min(usagePercent, 100)}%`,
    borderRadius: '999px',
    background: isUrgent ? '#EF4444' : '#F59E0B',
    transition: 'width 0.3s ease',
  };

  const btnStyle: React.CSSProperties = {
    background: 'transparent',
    border: `1px solid ${isUrgent ? 'rgba(239,68,68,0.5)' : 'rgba(245,158,11,0.5)'}`,
    color: isUrgent ? '#FCA5A5' : '#FCD34D',
    borderRadius: '6px',
    padding: '5px 12px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  };

  const message = isUrgent
    ? `Only ${freeGB} GB remaining — clear some files to keep uploading.`
    : `You're using ${usedGB} GB of ${limitGB} GB. Consider clearing some space.`;

  return (
    <div style={bannerStyle}>
      <span style={iconStyle}>{isUrgent ? '⚠️' : '💾'}</span>
      <span style={textStyle}>{message}</span>
      <div style={progressTrackStyle}>
        <div style={progressFillStyle} />
      </div>
      <button style={btnStyle} onClick={() => navigate('/library')}>
        Clear Space
      </button>
    </div>
  );
};

export default StorageWarningBanner;
